import { memo } from "react";

import { GitBranch } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { BranchMetadata } from "./BranchMetadata";

interface BranchInfo {
  id: string;
  name: string;
  branch_name?: string | null;
  is_branch?: boolean;
  parent_session_id?: string | null;
  new_messages?: number;
  memories_created?: number;
  last_activity_at?: string;
  archived?: boolean;
}

interface BranchIndicatorProps {
  currentSession: BranchInfo;
  branches: BranchInfo[];
  onSwitchBranch: (branchId: string) => void;
}

const BranchIndicatorComponent = ({ 
  currentSession, 
  branches,
  onSwitchBranch 
}: BranchIndicatorProps) => {
  if (!currentSession.is_branch && branches.length === 0) {
    return null;
  }

  const displayName = currentSession.branch_name || currentSession.name;
  const siblingBranches = branches.filter(
    b => b.id !== currentSession.id && b.parent_session_id === currentSession.parent_session_id
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="flex items-center gap-1.5">
          <GitBranch className="h-3 w-3" />
          <span className="font-medium">{displayName}</span>
        </Badge>
        
        {siblingBranches.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-xs">
                Switch Branch
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {siblingBranches.map((branch) => (
                <DropdownMenuItem
                  key={branch.id}
                  onClick={() => onSwitchBranch(branch.id)}
                  className="flex items-center gap-2"
                >
                  <GitBranch className="h-4 w-4" />
                  <span className="flex-1">{branch.branch_name || branch.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {currentSession.new_messages !== undefined && currentSession.last_activity_at && (
        <BranchMetadata
          newMessages={currentSession.new_messages}
          memoriesCreated={currentSession.memories_created || 0}
          lastActivityAt={currentSession.last_activity_at}
          archived={currentSession.archived}
          compact
        />
      )}
    </div>
  );
};

export const BranchIndicator = memo(BranchIndicatorComponent);
